import { useEffect, useState } from "preact/hooks";
import { api } from "./api";
import { isFeatureEnabled } from "./flags-core.ts";
import { startOfThisMonth } from "./prefetch";
import type { PeonStats } from "../shared/types";

/**
 * The signed-in peon's own numbers for the account sheet (tap your name): tasks completed, gold held
 * and rewards owed. The board already loads these with every summary, so it feeds them in here
 * (setAccountStats) and the sheet reads them without a request of its own. Only when the sheet opens
 * before any board has loaded (the app opened on Schedules, say) does it fetch them, once.
 */
export interface AccountStats {
  name: string;
  me: PeonStats;
  /** Rewards bought from this peon and not yet marked received; null when the economy is off. */
  owedCount: number | null;
}

let current: AccountStats | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

export function setAccountStats(stats: AccountStats): void {
  current = stats;
  for (const listener of listeners) listener();
}

export function getAccountStats(name: string): AccountStats | null {
  return current && current.name === name ? current : null;
}

function fetchOnce(name: string): void {
  if (inflight) return;
  inflight = api.board
    .summary({
      status: "open",
      name,
      since: startOfThisMonth(),
      claims: isFeatureEnabled("economyUI"),
      flash: false,
      notes: false,
    })
    .then((summary) => setAccountStats({ name, me: summary.me, owedCount: summary.owed_count }))
    .catch(() => {
      /* the sheet keeps its placeholder; the next board load fills it */
    })
    .finally(() => {
      inflight = null;
    });
}

/**
 * Reads the numbers again now (a buy in the character editor spent gold), so the account strip's
 * gold agrees with the wardrobe's balance. A read already in flight may predate the change, so a
 * fresh one follows it.
 */
export async function refreshAccountStats(name: string): Promise<void> {
  if (inflight) await inflight;
  fetchOnce(name);
  if (inflight) await inflight;
}

/** The latest numbers for `name`, following the board's live refreshes while the sheet is open. */
export function useAccountStats(name: string): AccountStats | null {
  const [stats, setStats] = useState(() => getAccountStats(name));
  useEffect(() => {
    const update = () => setStats(getAccountStats(name));
    listeners.add(update);
    update();
    if (!getAccountStats(name)) fetchOnce(name);
    return () => {
      listeners.delete(update);
    };
  }, [name]);
  return stats;
}
